/**
 * @fileoverview Unified comment navigation utilities for scrolling to different comment types
 * and handling various navigation scenarios across editor and renderer contexts.
 */
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { checkElementExpandInEditor, openInlineCommentInEditor } from './editorAnnotations';
import { watchForCommentRepliesAndScroll } from './domUtils';

// Navigation types based on comment scrolling architecture
enum NavigationType {
	INLINE_ANNOTATION = 'inline_annotation',
	COMMENTS_PANEL = 'comments_panel',
	FOOTER_COMMENTS = 'footer_comments',
	DEEP_LINK = 'deep_link',
	KEYBOARD_SHORTCUT = 'keyboard_shortcut',
	INLINE_HEADER_ARROWS = 'inline_header_arrows',
}

// Scroll behavior options
export enum ScrollBehavior {
	INSTANT = 'instant',
	SMOOTH = 'smooth',
}

// Navigation context
export enum NavigationContext {
	EDITOR = 'editor',
	RENDERER = 'renderer',
	PANEL = 'panel',
}

// Options for navigation
export type NavigationOptions = {
	navigationType: NavigationType;
	scrollBehavior?: ScrollBehavior;
	context: NavigationContext;
	block?: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement?: HTMLElement | null;
	onScrollReady?: (element: Element) => void;
	onFallback?: () => void;
};

// Scroll logical position types
type ScrollLogicalPosition = 'start' | 'center' | 'end' | 'nearest';

/**
 * WIP: Handle inline annotation navigation (editor vs renderer)
 */
export const handleInlineAnnotationNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
}): boolean => {
	const { scrollBehavior, context, block, inline, markElement } = options;

	if (!markElement) {
		return false;
	}

	// Handle different contexts
	switch (context) {
		case 'editor':
			openInlineCommentInEditor({
				targetElement: markElement,
				targetElementID: markElement.id,
				shouldScroll: true,
			});
			return true;
		case 'renderer':
			// Renderer-specific logic
			let focusedMarkerRef = document.querySelector("[data-has-focus='true']");

			if (focusedMarkerRef?.id !== markElement.id) {
				const emitter = getRendererAnnotationEventEmitter();

				setTimeout(() => {
					checkElementExpandInEditor(markElement);

					focusedMarkerRef = document.querySelector("[data-has-focus='true']");
					if (!focusedMarkerRef) {
						emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
							annotationIds: [markElement.id],
							eventTarget: markElement,
						});
						emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
							annotationId: markElement.id,
						});
					}
				}, 1000);
			}

			// Handle scroll behavior
			switch (scrollBehavior) {
				case 'instant':
					markElement.scrollIntoView({
						behavior: 'instant',
						block,
						inline,
					});
					break;
				case 'smooth':
					markElement.scrollIntoView({
						behavior: 'smooth',
						block,
						inline,
					});
					break;
			}
			return true;

		default:
			return false;
	}
};

/**
 * WIP: Handle comments panel navigation (inline + footer threads)
 */
export const handleCommentsPanelNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
	onScrollReady?: (element: Element) => void;
	onFallback?: () => void;
}): boolean => {
	const { context } = options;

	if (context !== 'panel') {
		return false;
	}
	return false;
};

/**
 * WIP: Handle footer comments navigation (general page-level threads)
 */
export const handleFooterCommentsNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
}): boolean => {
	const { context } = options;

	if (context !== 'panel') {
		return false;
	}

	return false;
};

/**
 * Handle deep link navigation (URL params)
 * Simple: scroll to comment once it's rendered on screen
 */
export const handleDeepLinkNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
	commentId: string;
}): boolean => {
	const { scrollBehavior, block, inline, commentId, markElement, context } = options;

	if (!commentId || !markElement) {
		return false;
	}

	// Check if comment already exists in DOM
	const existingComment = document.querySelector(`[data-comment-id="${commentId}"]`);

	if (existingComment) {
		// Comment is already rendered, scroll to it immediately
		existingComment.scrollIntoView({
			behavior: scrollBehavior,
			block,
			inline,
		});
		return true;
	} else {
		// we need to click on the annotation in the editor if the comment is not yet rendered
		if (context === NavigationContext.EDITOR && markElement) {
			openInlineCommentInEditor({
				targetElement: markElement,
				targetElementID: markElement.id,
				shouldScroll: false,
			});
		}
		// Comment not yet rendered, use DOM resolver to watch for it
		watchForCommentRepliesAndScroll({
			targetCommentId: commentId,
			onScrollReady: (commentElement) => {
				commentElement.scrollIntoView({
					behavior: scrollBehavior,
					block,
					inline,
				});
			},
			markElement,
		});
		return true;
	}

	return false;
};

/**
 * WIP: Handle keyboard shortcut navigation (next/previous/unread)
 */
export const handleKeyboardShortcutNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
}): boolean => {
	const { context } = options;

	if (context !== 'panel') {
		return false;
	}

	return false;
};

/**
 * WIP: Handle inline header arrows navigation (UI navigation)
 */
export const handleInlineHeaderArrowsNavigation = (options: {
	scrollBehavior: 'instant' | 'smooth';
	context: 'editor' | 'renderer' | 'panel';
	block: 'start' | 'center' | 'end' | 'nearest';
	inline: 'start' | 'center' | 'end' | 'nearest';
}): boolean => {
	const { context } = options;

	if (context !== 'panel') {
		return false;
	}

	return false;
};
